import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { API, getAuthToken } from "@/App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Badge } from "@/components/ui/badge";
import { Pencil, Trash2, FileCheck, Eye, AlertTriangle, Send, Clock, CheckCircle2, XCircle } from "lucide-react";
import { toast } from "sonner";
import { extractErrorMessage } from "@/lib/utils";

export default function Drafts() {
  const navigate = useNavigate();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const pageSize = 50;
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  // Debounce search so we don't hit the server on every keystroke; any search change → page 1.
  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(searchQuery); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchQuery]);

  const fetchDrafts = useCallback(async () => {
    try {
      setLoading(true);
      const params = { page, page_size: pageSize };
      if (debouncedSearch) params.search = debouncedSearch;
      const response = await axios.get(`${API}/drafts`, {
        params,
        headers: { Authorization: `Bearer ${getAuthToken()}` },
      });
      setDrafts(response.data.items || []);
      setTotalCount(response.data.total || 0);
      setTotalPages(response.data.total_pages || 0);
    } catch (error) {
      toast.error("Error fetching drafts");
    } finally {
      setLoading(false);
    }
  }, [page, debouncedSearch]);

  useEffect(() => {
    fetchDrafts();
  }, [fetchDrafts]);

  const handleDelete = async (id) => {
    try {
      await axios.delete(`${API}/quotations/${id}`, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      toast.success("Draft deleted successfully");
      fetchDrafts();
    } catch (error) {
      toast.error("Error deleting draft");
    }
  };

  const handleFinalize = async (id) => {
    try {
      await axios.put(`${API}/drafts/${id}/finalize`, {}, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      toast.success("Draft converted to quotation successfully");
      fetchDrafts();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error finalizing draft"));
    }
  };

  const handleSubmitApproval = async (id) => {
    try {
      await axios.post(`${API}/quotations/${id}/submit-approval`, {}, {
        headers: { Authorization: `Bearer ${getAuthToken()}` }
      });
      toast.success("Submitted for manager approval");
      fetchDrafts();
    } catch (error) {
      toast.error(extractErrorMessage(error, "Error submitting for approval"));
    }
  };

  // Approval badge for a draft based on its workflow status.
  const approvalBadge = (status, needsApproval) => {
    if (status === "pending") return <Badge className="bg-amber-100 text-amber-800 hover:bg-amber-100"><Clock className="h-3 w-3 mr-1" />Pending Approval</Badge>;
    if (status === "approved") return <Badge className="bg-green-100 text-green-800 hover:bg-green-100"><CheckCircle2 className="h-3 w-3 mr-1" />Approved</Badge>;
    if (status === "rejected") return <Badge className="bg-red-100 text-red-800 hover:bg-red-100"><XCircle className="h-3 w-3 mr-1" />Rejected</Badge>;
    if (needsApproval) return <Badge variant="secondary" className="bg-slate-100 text-slate-700">Needs Approval</Badge>;
    return <Badge variant="secondary" className="bg-slate-100 text-slate-600">Draft</Badge>;
  };

  const formatDate = (dateString) => {
    if (!dateString) return "-";
    return new Date(dateString).toLocaleDateString("en-IN", {
      day: "2-digit",
      month: "short",
      year: "numeric"
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-accent"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-semibold text-[#0F0F0F]" data-testid="drafts-title">Drafts</h1>
          <p className="text-muted-foreground">Manage your saved draft quotations</p>
        </div>
        <Button
          onClick={() => navigate("/quotations/create")}
          className="bg-accent hover:bg-accent/90 text-white"
          data-testid="create-quotation-button"
        >
          Create New Quotation
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Drafts ({totalCount})</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="mb-4">
            <Input
              placeholder="Search by Draft ID or Seller..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="max-w-sm"
              data-testid="drafts-search-input"
            />
          </div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Draft ID</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Seller</TableHead>
                  <TableHead>Total MRP</TableHead>
                  <TableHead>Invoice Value</TableHead>
                  <TableHead>Items</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {drafts.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      No drafts found. Save a quotation as draft to see it here.
                    </TableCell>
                  </TableRow>
                ) : (
                  drafts.map((draft) => {
                    const status = draft.approval_status || null;
                    const needsApproval = !!draft.needs_approval;
                    const reasons = draft.approval_reasons || [];
                    const canFinalize = !needsApproval || status === "approved";
                    const canSubmit = needsApproval && (status === null || status === "rejected");
                    const rejectionRemarks = draft.approval_info?.rejection_remarks;
                    return (
                    <TableRow key={draft.id} data-testid={`draft-row-${draft.id}`}>
                      <TableCell className="font-mono font-medium">
                        <Badge variant="secondary" className="bg-amber-100 text-amber-800">
                          {draft.quotation_id}
                        </Badge>
                      </TableCell>
                      <TableCell>{formatDate(draft.order_date)}</TableCell>
                      <TableCell>
                        <div>
                          <div className="font-medium">{draft.seller_name}</div>
                          <div className="text-xs text-muted-foreground">{draft.seller_shipping_address?.substring(0, 30)}...</div>
                        </div>
                      </TableCell>
                      <TableCell className="font-mono">
                        ₹{draft.total_mrp_value?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || "0"}
                      </TableCell>
                      <TableCell className="font-mono">
                        ₹{draft.total_invoice_value?.toLocaleString('en-IN', { maximumFractionDigits: 2 }) || "0"}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">
                          {draft.items_count || 0} items, {draft.freebies_count || 0} freebies
                        </span>
                      </TableCell>
                      <TableCell data-testid={`draft-status-${draft.id}`}>
                        <div className="flex flex-col gap-1">
                          {approvalBadge(status, needsApproval)}
                          {needsApproval && status !== "approved" && reasons.length > 0 && (
                            <span className="text-[11px] text-amber-700 max-w-[190px]" data-testid={`draft-reasons-${draft.id}`}>
                              {reasons.join(" + ")}
                            </span>
                          )}
                          {status === "rejected" && rejectionRemarks && (
                            <span className="text-[11px] text-red-600 max-w-[160px] truncate" title={rejectionRemarks}>
                              “{rejectionRemarks}”
                            </span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <div className="flex justify-end gap-2">
                          {canSubmit && (
                            <Button
                              size="sm"
                              variant="ghost"
                              className="text-amber-700 hover:text-amber-800"
                              onClick={() => handleSubmitApproval(draft.id)}
                              title="Submit for Manager Approval"
                              data-testid={`submit-approval-${draft.id}`}
                            >
                              <Send className="h-4 w-4" />
                            </Button>
                          )}
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/quotations/${draft.id}`)}
                            title="View"
                            data-testid={`view-draft-${draft.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => navigate(`/quotations/${draft.id}/edit`)}
                            title="Edit"
                            data-testid={`edit-draft-${draft.id}`}
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                          {canFinalize && (
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-accent hover:text-accent"
                                title="Convert to Final"
                                data-testid={`finalize-draft-${draft.id}`}
                              >
                                <FileCheck className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Convert to Final Quotation?</AlertDialogTitle>
                                <AlertDialogDescription asChild>
                                  <div className="space-y-2">
                                    <p>This will convert draft {draft.quotation_id} to a final quotation. 
                                    It will appear in the Quotations Log and can be emailed to clients.</p>
                                    {(() => {
                                      const warnings = [];
                                      if (!draft.transporter_name) warnings.push("Transporter is missing");
                                      if (draft.final_status === "over_discount" && !draft.approval_override) warnings.push("Over-discount approval is required");
                                      if (draft.final_status === "over_discount" && draft.approval_override && !draft.approval_remarks) warnings.push("Approval remarks are missing");
                                      return warnings.length > 0 ? (
                                        <div className="mt-2 p-3 bg-amber-50 border border-amber-200 rounded-lg">
                                          <div className="flex items-center gap-2 text-amber-800 font-medium text-sm mb-1">
                                            <AlertTriangle className="h-4 w-4" />
                                            Cannot finalize - missing required fields:
                                          </div>
                                          <ul className="list-disc list-inside text-sm text-amber-700">
                                            {warnings.map((w, i) => <li key={i}>{w}</li>)}
                                          </ul>
                                          <p className="text-xs text-amber-600 mt-1">Please edit the draft to fill in these fields before finalizing.</p>
                                        </div>
                                      ) : null;
                                    })()}
                                  </div>
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleFinalize(draft.id)}
                                  className="bg-accent hover:bg-accent/90"
                                >
                                  Convert to Final
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                          )}
                          {needsApproval && status === "pending" && (
                            <Button size="sm" variant="ghost" className="text-amber-600 cursor-default" disabled title="Awaiting manager approval" data-testid={`pending-approval-${draft.id}`}>
                              <Clock className="h-4 w-4" />
                            </Button>
                          )}
                          <AlertDialog>
                            <AlertDialogTrigger asChild>
                              <Button
                                size="sm"
                                variant="ghost"
                                className="text-destructive hover:text-destructive"
                                title="Delete"
                                data-testid={`delete-draft-${draft.id}`}
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </AlertDialogTrigger>
                            <AlertDialogContent>
                              <AlertDialogHeader>
                                <AlertDialogTitle>Delete Draft?</AlertDialogTitle>
                                <AlertDialogDescription>
                                  This action cannot be undone. This will permanently delete draft {draft.quotation_id}.
                                </AlertDialogDescription>
                              </AlertDialogHeader>
                              <AlertDialogFooter>
                                <AlertDialogCancel>Cancel</AlertDialogCancel>
                                <AlertDialogAction
                                  onClick={() => handleDelete(draft.id)}
                                  className="bg-destructive hover:bg-destructive/90"
                                >
                                  Delete
                                </AlertDialogAction>
                              </AlertDialogFooter>
                            </AlertDialogContent>
                          </AlertDialog>
                        </div>
                      </TableCell>
                    </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-4">
              <span className="text-sm text-muted-foreground" data-testid="drafts-page-info">
                Page {page} of {totalPages} · {totalCount} drafts
              </span>
              <div className="flex gap-2">
                <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} data-testid="drafts-prev-page">
                  Previous
                </Button>
                <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage((p) => Math.min(totalPages, p + 1))} data-testid="drafts-next-page">
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
